import { useSearchParams, useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import {
	Button,
	TextField,
	Typography,
	Paper,
	CircularProgress,
	IconButton,
	InputAdornment
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import _ from 'lodash';
import Link from '@fuse/core/Link';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
// Image - use regular img tag
import { toast } from 'react-toastify';
import { useResetPasswordMutation } from '@/store/services/auth';
import { useState } from 'react';

/**
 * Form Validation Schema
 */
const schema = z
	.object({
		password: z
			.string()
			.nonempty('Please enter your password.')
			.min(8, 'Password is too short - should be 8 chars minimum.'),
		password_confirmation: z.string().nonempty('Password confirmation is required')
	})
	.refine((data) => data.password === data.password_confirmation, {
		message: 'Passwords must match',
		path: ['password_confirmation']
	});

type FormType = {
	password: string;
	password_confirmation: string;
};

const defaultValues = {
	password: '',
	password_confirmation: ''
};

/**
 * The classic reset password page.
 */
function ClassicResetPasswordPage() {
	const [searchParams] = useSearchParams();
	// URLSearchParams treats "+" as space for query values. Normalize back for email aliases.
	const email = searchParams.get('email')?.replace(/ /g, '+');
	const token = searchParams.get('token');
	const navigate = useNavigate();
	const [showPassword, setShowPassword] = useState(false);
	const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);

	const [resetPassword, { isLoading }] = useResetPasswordMutation();

	const { control, formState, handleSubmit, reset } = useForm({
		mode: 'onChange',
		defaultValues,
		resolver: zodResolver(schema)
	});

	const { isValid, dirtyFields, errors } = formState;

	async function onSubmit(formData: FormType) {
		if (!email || !token) return;

		const { password, password_confirmation } = formData;

		const result = await resetPassword({
			email,
			token,
			password,
			password_confirmation
		});

		if (!result.error) {
			toast.success('Password reset successfully.');
			navigate('/sign-in');
			reset(defaultValues);
		} else {
			const error = result.error as any;
			const errorData = error?.data;
			toast.error(errorData?.errors?.[0]?.detail || 'Something went wrong');
		}
	}

	return (
		<div className="flex min-w-0 flex-auto flex-col items-center sm:justify-center">
			<Paper className="bg-white min-h-full w-full rounded-none px-4 py-8 sm:min-h-auto sm:w-auto sm:rounded-xl sm:p-12 sm:shadow-sm">
				<div className="mx-auto w-full max-w-80 sm:mx-0 sm:w-80 flex flex-col items-center">
					<img
						src="/assets/images/logo/logo_loanapp.png"
						alt="ASSETS ID"
						width={165}
						height={70}
					/>

					<div className="w-full">
						<Typography className="mt-8 text-4xl font-extrabold leading-[1.25] tracking-tight">
							Reset your password
						</Typography>
						<Typography className="font-medium">Create a new password for your account.</Typography>

						<form
							name="registerForm"
							noValidate
							className="mt-8 flex w-full flex-col justify-center"
							onSubmit={handleSubmit(onSubmit)}
						>
							<Controller
								name="password"
								control={control}
								render={({ field }) => (
									<TextField
										{...field}
										className="mb-6"
										label="Password"
										type={showPassword ? 'text' : 'password'}
										error={!!errors.password}
										helperText={errors?.password?.message}
										variant="outlined"
										required
										fullWidth
										InputProps={{
											endAdornment: (
												<InputAdornment position="end">
													<IconButton
														aria-label={showPassword ? 'Hide password' : 'Show password'}
														onClick={() => setShowPassword((show) => !show)}
														onMouseDown={(event) => event.preventDefault()}
														edge="end"
													>
														{showPassword ? (
															<VisibilityOff sx={{ fontSize: 19 }} />
														) : (
															<Visibility sx={{ fontSize: 19 }} />
														)}
													</IconButton>
												</InputAdornment>
											)
										}}
									/>
								)}
							/>

							<Controller
								name="password_confirmation"
								control={control}
								render={({ field }) => (
									<TextField
										{...field}
										className="mb-6"
										label="Password (Confirm)"
										type={showPasswordConfirmation ? 'text' : 'password'}
										error={!!errors.password_confirmation}
										helperText={errors?.password_confirmation?.message}
										variant="outlined"
										required
										fullWidth
										InputProps={{
											endAdornment: (
												<InputAdornment position="end">
													<IconButton
														aria-label={
															showPasswordConfirmation ? 'Hide password' : 'Show password'
														}
														onClick={() => setShowPasswordConfirmation((show) => !show)}
														onMouseDown={(event) => event.preventDefault()}
														edge="end"
													>
														{showPasswordConfirmation ? (
															<VisibilityOff sx={{ fontSize: 19 }} />
														) : (
															<Visibility sx={{ fontSize: 19 }} />
														)}
													</IconButton>
												</InputAdornment>
											)
										}}
									/>
								)}
							/>

							<Button
								variant="contained"
								color="secondary"
								className=" mt-1 w-full"
								aria-label="Register"
								disabled={isLoading || _.isEmpty(dirtyFields) || !isValid}
								type="submit"
								size="large"
								startIcon={isLoading && <CircularProgress size={20} />}
							>
								Reset your password
							</Button>

							<Typography
								className="mt-8 text-md font-medium"
								color="text.secondary"
							>
								<span>Return to</span>
								<Link
									className="ml-1"
									to="/sign-in"
								>
									sign in
								</Link>
							</Typography>
						</form>
					</div>
				</div>
			</Paper>
		</div>
	);
}

export default ClassicResetPasswordPage;
